<div
  class="dsg-builder"
  #builderRoot
  [class.is-preview]="previewMode()"
  [style.top.px]="previewMode() ? previewInset().top : null"
  [style.left.px]="previewMode() ? previewInset().left : null"
>
  <!-- ══════════════════ Toolbar ══════════════════ -->
  <header class="dsg-toolbar" *ngIf="!previewMode()">
    <div class="dsg-toolbar-group dsg-tools">
      <button type="button" class="dsg-tool" [class.is-active]="activeTool() === 'select'" (click)="setTool('select')" matTooltip="Select (V)">
        <mat-icon>near_me</mat-icon>
      </button>
      <button type="button" class="dsg-tool" [class.is-active]="activeTool() === 'hand'" (click)="setTool('hand')" matTooltip="Pan (H · or hold Space)">
        <mat-icon>pan_tool</mat-icon>
      </button>
      <span class="dsg-toolbar-divider"></span>

      <!-- Shape tools live in one compact menu; the button shows the armed one. -->
      <button
        type="button"
        class="dsg-tool dsg-shape-menu-btn"
        [class.is-active]="isShapeToolActive()"
        [matMenuTriggerFor]="shapeMenu"
        matTooltip="Shapes"
        aria-label="Shape tools"
      >
        <mat-icon>{{ shapeToolIcon() }}</mat-icon>
        <mat-icon class="dsg-tool-caret">arrow_drop_down</mat-icon>
      </button>
      <mat-menu #shapeMenu="matMenu">
        <button mat-menu-item *ngFor="let tool of shapeTools" (click)="setTool(tool.key)">
          <mat-icon>{{ tool.icon }}</mat-icon>
          <span>{{ tool.label }}</span>
        </button>
      </mat-menu>
    </div>

    <div class="dsg-toolbar-group">
      <button type="button" class="dsg-tool" [disabled]="!doc.canUndo()" (click)="doc.undo()" matTooltip="Undo (Ctrl+Z)">
        <mat-icon>undo</mat-icon>
      </button>
      <button type="button" class="dsg-tool" [disabled]="!doc.canRedo()" (click)="doc.redo()" matTooltip="Redo (Ctrl+Shift+Z)">
        <mat-icon>redo</mat-icon>
      </button>
      <span class="dsg-toolbar-divider"></span>
      <button type="button" class="dsg-tool" [disabled]="!canGroup()" (click)="doc.groupSelected()" matTooltip="Group (Ctrl+G)">
        <mat-icon>folder</mat-icon>
      </button>
      <button type="button" class="dsg-tool" [disabled]="!hasSelection()" (click)="doc.ungroupSelected()" matTooltip="Ungroup (Ctrl+Shift+G)">
        <mat-icon>call_split</mat-icon>
      </button>
      <button type="button" class="dsg-tool" [disabled]="!hasSelection()" (click)="doc.duplicateSelected()" matTooltip="Duplicate (Ctrl+D)">
        <mat-icon>content_copy</mat-icon>
      </button>
      <button type="button" class="dsg-tool" [disabled]="!hasSelection()" (click)="doc.deleteSelected()" matTooltip="Delete">
        <mat-icon>delete_outline</mat-icon>
      </button>
      <span class="dsg-toolbar-divider"></span>
      <!-- Layout grid: the button toggles it, the caret picks size and snap. -->
      <button
        type="button"
        class="dsg-tool"
        [class.is-active]="layoutGrid()"
        (click)="toggleLayoutGrid()"
        [matTooltip]="layoutGrid() ? 'Hide layout grid (Ctrl+\')' : 'Show layout grid (Ctrl+\')'"
        aria-label="Toggle layout grid"
      >
        <mat-icon>{{ layoutGrid() ? 'grid_on' : 'grid_off' }}</mat-icon>
      </button>
      <button
        type="button"
        class="dsg-tool dsg-grid-menu-btn"
        [matMenuTriggerFor]="gridMenu"
        matTooltip="Grid settings"
        aria-label="Grid settings"
      >
        <mat-icon class="dsg-tool-caret">arrow_drop_down</mat-icon>
      </button>
      <mat-menu #gridMenu="matMenu">
        <button mat-menu-item (click)="toggleGridSnap()">
          <mat-icon>{{ grid().snap ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
          <span>Snap to grid</span>
        </button>
        <button mat-menu-item *ngFor="let size of gridSizes" (click)="setGridSize(size)">
          <mat-icon>{{ grid().size === size ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
          <span>{{ size }} px grid</span>
        </button>
      </mat-menu>
    </div>

    <!-- Project ▸ dashboard. The project is the PARENT: one project owns the
         several dashboards built for it. -->
    <div class="dsg-identity">
      <select
        class="dsg-project"
        [ngModel]="currentProjectId()"
        (ngModelChange)="onProjectChange($event)"
        matTooltip="Project this dashboard belongs to"
        aria-label="Project"
      >
        <option value="">No project</option>
        <option *ngFor="let project of allProjects()" [value]="project.id">{{ project.name }}</option>
      </select>
      <button type="button" class="dsg-tool" (click)="createProject()" matTooltip="New project">
        <mat-icon>create_new_folder</mat-icon>
      </button>
      <span class="dsg-identity-sep">/</span>
      <input
        class="dsg-name"
        type="text"
        [ngModel]="designName()"
        (ngModelChange)="setName($event)"
        aria-label="Dashboard name"
        placeholder="Untitled dashboard"
      />
    </div>
    <span class="dsg-status" [class.is-published]="status() === 'published'">{{ status() }}</span>
    <!-- Root frames are what gets published, so the count is worth showing. -->
    <span
      class="dsg-screens"
      [matTooltip]="screens().length ? 'Each root Frame publishes as one screen' : 'Draw a Frame (F) to define a screen'"
    >
      {{ screens().length }} {{ screens().length === 1 ? 'screen' : 'screens' }}
    </span>

    <div class="dsg-toolbar-group dsg-zoom">
      <button type="button" class="dsg-tool" (click)="zoomOut()" matTooltip="Zoom out">
        <mat-icon>remove</mat-icon>
      </button>
      <button type="button" class="dsg-zoom-value" (click)="zoomReset()" matTooltip="Reset to 100% (Ctrl+1)">
        {{ zoomPercent() }}%
      </button>
      <button type="button" class="dsg-tool" (click)="zoomIn()" matTooltip="Zoom in">
        <mat-icon>add</mat-icon>
      </button>
      <button type="button" class="dsg-tool" (click)="zoomFit()" matTooltip="Zoom to fit (Ctrl+0)">
        <mat-icon>fit_screen</mat-icon>
      </button>
    </div>

    <div class="dsg-toolbar-group dsg-actions">
      <button type="button" class="dsg-btn" (click)="newDesign()" matTooltip="Start a new design">
        <mat-icon>note_add</mat-icon>
      </button>
      <!-- Labelled and tinted: the saved design templates are the starting
           point for most work, so this should not read as another icon. -->
      <button
        type="button"
        class="dsg-btn dsg-btn-templates"
        (click)="openTemplatesDialog()"
        matTooltip="Browse and open saved design templates"
      >
        <mat-icon>dashboard_customize</mat-icon>
        Templates
        <span class="dsg-btn-count" *ngIf="templates().length">{{ templates().length }}</span>
      </button>
      <button type="button" class="dsg-btn" [class.is-active]="previewMode()" (click)="togglePreview()">
        <mat-icon>{{ previewMode() ? 'edit' : 'visibility' }}</mat-icon>
        {{ previewMode() ? 'Edit' : 'Preview' }}
      </button>
      <button type="button" class="dsg-btn" [disabled]="saving()" (click)="save()">
        <mat-icon>save</mat-icon> {{ saving() ? 'Saving...' : 'Save' }}
      </button>
      <button type="button" class="dsg-btn" [disabled]="saving()" (click)="saveAs()" matTooltip="Create a new dashboard from the current design">
        <mat-icon>save_as</mat-icon> Save As
      </button>

      <!-- As in the Layout Builder: Publish, then Update once it is live.
           Unbound widgets are confirmed in publish(), never shipped silently. -->
      <button
        type="button"
        class="dsg-btn dsg-btn-publish"
        [disabled]="saving()"
        (click)="publish()"
        [matTooltip]="status() === 'published' ? 'Update the published dashboard' : 'Publish the dashboard to the left menu'"
      >
        <mat-icon>{{ status() === 'published' ? 'sync' : 'publish' }}</mat-icon>
        {{ status() === 'published' ? 'Update' : 'Publish' }}
      </button>
    </div>
  </header>

  <!-- ══════════════════ Body: three panes ══════════════════ -->
  <div class="dsg-body">
    <!-- Left: layers on top, palette below. -->
    <aside class="dsg-pane dsg-pane-left" *ngIf="!previewMode()"
      [class.is-collapsed]="leftPaneCollapsed()"
      [class.is-resizing]="resizingPane() === 'left'"
      [style.width.px]="leftPaneCollapsed() ? 36 : leftPaneWidth()">
      <div class="dsg-pane-resizer" *ngIf="!leftPaneCollapsed()"
        (pointerdown)="startPaneResize('left', $event)" (dblclick)="resetPaneWidth('left')"
        matTooltip="Drag to resize · double-click to reset" matTooltipPosition="right"
        role="separator" aria-orientation="vertical" aria-label="Resize layers and objects panel"></div>
      <button type="button" class="dsg-pane-rail" *ngIf="leftPaneCollapsed()"
        (click)="togglePane('left')" matTooltip="Open layers & objects" matTooltipPosition="right"
        aria-label="Open layers and objects panel">
        <mat-icon>chevron_right</mat-icon>
        <span>Layers &amp; Objects</span>
      </button>
      <section class="dsg-pane-block dsg-pane-layers">
        <h3 class="dsg-pane-title dsg-pane-title-row">
          <span>Layers</span>
          <button type="button" class="dsg-pane-toggle" (click)="togglePane('left')"
            matTooltip="Collapse panel" aria-label="Collapse layers and objects panel">
            <mat-icon>chevron_left</mat-icon>
          </button>
        </h3>
        <app-design-layers-panel
          [nodes]="nodes()"
          [selectedIds]="selectedIds()"
          [activeLayerId]="doc.activeLayerId()"
          (selectNode)="onLayerSelect($event)"
          (toggleVisible)="doc.toggleVisible($event)"
          (toggleLocked)="doc.toggleLocked($event)"
          (renameNode)="doc.rename($event.id, $event.name)"
          (deleteNode)="doc.select($event); doc.deleteSelected()"
          (reorderNode)="onLayerReorder($event)"
          (selectLayer)="onLayerBandSelect($event)"
          (renameLayer)="doc.renameLayer($event.id, $event.name)"
          (setLayerFlag)="doc.setLayerFlag($event.id, $event.flag, $event.value)"
          (deleteLayer)="doc.deleteLayer($event)"
          (addLayer)="doc.addLayer($event.screenId, $event.kind)"
          (moveLayer)="doc.moveLayerTo($event.dragId, $event.targetId, $event.position)"
          (moveNodeToLayer)="doc.moveNodeToLayer($event.nodeId, $event.layerId)"
        ></app-design-layers-panel>
      </section>

      <!-- Components and Icons share the lower half as tabs — three stacked
           scroll areas in a 260px pane would leave none of them usable. -->
      <section class="dsg-pane-block dsg-pane-palette">
        <div class="dsg-tabs dsg-tabs-left">
          <button type="button" [class.is-active]="leftTab() === 'components'" (click)="setLeftTab('components')">
            Objects
          </button>
          <button type="button" [class.is-active]="leftTab() === 'icons'" (click)="setLeftTab('icons')">
            Icons
          </button>
        </div>

        <div class="dsg-tab-body" [hidden]="leftTab() !== 'components'">
          <app-design-component-palette
            [palette]="palette()"
            [activeTool]="activeTool()"
            (itemPicked)="onPaletteItemPicked($event)"
          ></app-design-component-palette>
        </div>

        <div class="dsg-tab-body" [hidden]="leftTab() !== 'icons'">
          <app-design-icon-library
            [activeIconName]="selectedIconName()"
            (iconPicked)="onIconPicked($event)"
          ></app-design-icon-library>
        </div>
      </section>
    </aside>

    <!-- Centre: the canvas. -->
    <main class="dsg-pane dsg-pane-canvas">
      <!-- Floated over the canvas rather than added to the toolbar, which is
           already full. It belongs here anyway: switching mode changes what the
           canvas shows, not what the toolbar does. -->
      <div class="dsg-mode-switch" role="group" aria-label="Builder mode" *ngIf="!previewMode()">
        <button
          type="button"
          [class.is-active]="mode() === 'design'"
          (click)="setMode('design')"
          matTooltip="Lay out the screen"
        >
          <mat-icon>design_services</mat-icon> Design
        </button>
        <button
          type="button"
          [class.is-active]="mode() === 'data'"
          (click)="setMode('data')"
          matTooltip="Connect components to real data"
        >
          <mat-icon>database</mat-icon> Data
          <span class="dsg-mode-count" *ngIf="unboundCount()">{{ unboundCount() }}</span>
        </button>
      </div>
      <app-design-canvas *ngIf="!previewMode()" [palette]="palette()" [hideSample]="mode() === 'data'"></app-design-canvas>
      <app-design-screen-preview
        *ngIf="previewMode() && previewScreen() as screen"
        [screen]="screen"
        mode="fit-width"
      ></app-design-screen-preview>
      <!-- A button's Open popup → screen (on the canvas and in Preview). -->
      <!-- Editable on the canvas only: drag to place it, resize from the corner —
           saved on the button. Preview shows it exactly as published. -->
      <app-design-screen-popup [editable]="!previewMode()"></app-design-screen-popup>
    </main>

    <!-- Right: Design | JSON | Data. Templates open from the toolbar. -->
    <!-- Hidden, NOT destroyed, in Preview: it hosts the build steps the canvas
         runtime applies recompute results with, so filters keep working there. -->
    <aside class="dsg-pane dsg-pane-right" [class.is-preview-hidden]="previewMode()"
      [class.is-collapsed]="rightPaneCollapsed()"
      [class.is-resizing]="resizingPane() === 'right'"
      [style.width.px]="rightPaneCollapsed() ? 36 : rightPaneWidth()">
      <div class="dsg-pane-resizer" *ngIf="!rightPaneCollapsed()"
        (pointerdown)="startPaneResize('right', $event)" (dblclick)="resetPaneWidth('right')"
        matTooltip="Drag to resize · double-click to reset" matTooltipPosition="left"
        role="separator" aria-orientation="vertical" aria-label="Resize design, data and JSON panel"></div>
      <button type="button" class="dsg-pane-rail" *ngIf="rightPaneCollapsed()"
        (click)="togglePane('right')" matTooltip="Open design, data & JSON" matTooltipPosition="left"
        aria-label="Open design, data and JSON panel">
        <mat-icon>chevron_left</mat-icon>
        <span>Design · Data · JSON</span>
      </button>
      <div class="dsg-tabs">
        <button type="button" class="dsg-pane-toggle" (click)="togglePane('right')"
          matTooltip="Collapse panel" aria-label="Collapse design, data and JSON panel">
          <mat-icon>chevron_right</mat-icon>
        </button>
        <!-- Design is hidden while connecting data: that phase is about fields,
             not fills, and the mode switch is how you go back. -->
        <button type="button" *ngIf="mode() === 'design'" [class.is-active]="rightTab() === 'design'" (click)="setMode('design')">Design</button>
        <button type="button" *ngIf="mode() === 'data'" [class.is-active]="rightTab() === 'data'" (click)="setMode('data')">Data</button>
        <button type="button" [class.is-active]="rightTab() === 'json'" (click)="setRightTab('json')">JSON</button>
      </div>

      <div class="dsg-tab-body" [hidden]="rightTab() !== 'design'">
        <app-design-inspector
          [layoutConfig]="layoutConfig()"
        ></app-design-inspector>
      </div>

      <!-- Kept mounted so switching tabs never loses an in-progress JSON edit. -->
      <div class="dsg-tab-body" [hidden]="rightTab() !== 'json'">
        <app-design-json-panel></app-design-json-panel>
      </div>
      <div class="dsg-tab-body dsg-tab-scroll" [hidden]="rightTab() !== 'data'">
        <app-design-data-panel
          [selected]="doc.selectedNode()"
          (columnPicked)="bindSelectedText($event)"
          (connectText)="connectSelectedText($event)"
          (unbind)="unbindSelectedText()"
          (widgetBuilt)="applyWidgetBuild($event.nodeId, $event.tile)"
          (widgetBindingChange)="applyWidgetBinding($event.nodeId, $event.binding)"
          (widgetGenerate)="generateZoneSlider($event.nodeId, $event.binding)"
        ></app-design-data-panel>
      </div>
    </aside>
  </div>

  <button
    *ngIf="previewMode()"
    type="button"
    class="dsg-preview-exit"
    (click)="togglePreview()"
    aria-label="Exit preview"
    matTooltip="Back to editor (Esc)"
  >
    <mat-icon>close</mat-icon>
    Exit preview
  </button>
</div>
